import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { ReactGrid, CellChange, TextCell, NumberCell, Id } from '@silevis/reactgrid';
import '@silevis/reactgrid/styles.css';
import { Box } from '@mui/material';
import {
    ADD_LIST_COLUMN_ID,
    FIXED_COLUMNS_COUNT,
    FIXED_DATA_COLUMN_IDS,
    PriceGridRow,
    buildPriceGridColumns,
    buildPriceGridRows,
    setCellPrice,
} from './priceListGridModel';
import { ACTION_COLUMN_ID, ADD_ROW_FOOTER_ID, gridContainerSx, useColumnWidths, useEditableGrid } from './editableGrid';

/**
 * SHER-1128 (SHER-308 Phase 4) — the price tab's grid.
 *
 * Same ReactGrid as today's BillablesConfiguration (fixed columns, delete toggle, add-row
 * footer, resizable columns, copy), but the data columns are the active PriceLists instead
 * of one column per insurer. Adding a list = adding a column (Decision C), driven from the
 * trailing add-list column's footer cell — the column mirror of the add-row footer.
 *
 * Presentational and self-contained: via useEditableGrid it seeds its own working state from
 * props so the catalog is interactive, and reports edits upward. The Billing route feeds it the
 * per-list CSV and wires Save/Export/Import.
 */
export interface PriceListGridProps {
    priceLists: string[];
    rows: PriceGridRow[];
    /** Reports the working rows after every edit (add/delete/price change). */
    onRowsChange?: (rows: PriceGridRow[]) => void;
    /** Reports the working list codes after a column is added. */
    onPriceListsChange?: (priceLists: string[]) => void;
}

const PriceListGrid: React.FC<PriceListGridProps> = ({
    priceLists,
    rows,
    onRowsChange,
    onPriceListsChange,
}) => {
    const [listCodes, setListCodes] = useState<string[]>(priceLists);
    const { columnWidths, handleColumnResize } = useColumnWidths();

    // Re-seed the list columns from props when the container swaps the data (same semantics as the
    // rows re-seed in useEditableGrid).
    useEffect(() => { setListCodes(priceLists); }, [priceLists]);

    // SHER-1128: a new row starts with no price on any list (empty cells), not 0 on every list.
    const makeNewRow = useCallback((): PriceGridRow => (
        { serviceType: '', category: '', serviceCode: '', department: '', name: '', billableType: '', available: '', prices: {} }
    ), []);

    const isRowEmpty = useCallback((row: PriceGridRow): boolean => {
        // SHER-1128: an absent key is "no price"; any present key, even a typed 0, is content.
        const hasPrice = Object.keys(row.prices).length > 0;
        return !row.serviceType?.trim() && !row.category?.trim() && !row.serviceCode?.trim()
            && !row.department?.trim() && !row.name?.trim() && !row.billableType?.trim()
            && !row.available?.trim() && !hasPrice;
    }, []);

    const { gridRows, commitRows, onActionFocus } = useEditableGrid<PriceGridRow>(rows, onRowsChange, { makeNewRow, isRowEmpty });

    const columns = useMemo(
        () => buildPriceGridColumns(listCodes, columnWidths),
        [listCodes, columnWidths],
    );
    const gridModel = useMemo(
        () => buildPriceGridRows(gridRows, listCodes),
        [gridRows, listCodes],
    );

    const handleChanges = useCallback((changes: CellChange[]) => {
        const next = gridRows.map((r) => ({ ...r, prices: { ...r.prices } }));
        changes.forEach((change) => {
            const rowIndex = change.rowId as number;
            const columnId = change.columnId as string;
            if (typeof rowIndex !== 'number' || !next[rowIndex]) return;

            if (columnId === ACTION_COLUMN_ID) {
                // Guard: a text edit on the action cell toggles DELETE, never writes a price column.
                next[rowIndex].action = next[rowIndex].action === 'DELETE' ? '' : 'DELETE';
            } else if ((FIXED_DATA_COLUMN_IDS as readonly string[]).includes(columnId)) {
                const cell = change.newCell as TextCell;
                (next[rowIndex] as any)[columnId] = cell.text;
            } else if (columnId !== ADD_LIST_COLUMN_ID) {
                const cell = change.newCell as NumberCell;
                setCellPrice(next[rowIndex].prices, columnId, cell.value);
            }
        });
        commitRows(next);
    }, [gridRows, commitRows]);

    const handleFocusLocationChange = useCallback((location: { rowId: Id; columnId: Id }) => {
        // Add a new list = add a column (Decision C): the add-list footer cell, checked before the
        // shared action-column dispatch (it uses a different column, so order is only for clarity).
        if (location.rowId === ADD_ROW_FOOTER_ID && location.columnId === ADD_LIST_COLUMN_ID) {
            const code = (typeof window !== 'undefined' ? window.prompt('New price list code') : '')?.trim();
            if (!code || listCodes.includes(code)) return;
            const next = [...listCodes, code];
            setListCodes(next);
            onPriceListsChange?.(next);
            return;
        }
        onActionFocus(location);
    }, [listCodes, onActionFocus, onPriceListsChange]);

    return (
        <Box id="price-list-grid-container" sx={gridContainerSx}>
            <ReactGrid
                rows={gridModel}
                columns={columns}
                onCellsChanged={handleChanges}
                onFocusLocationChanged={handleFocusLocationChange}
                onColumnResized={handleColumnResize}
                enableRangeSelection
                enableFillHandle
                enableColumnSelection
                enableRowSelection
                stickyTopRows={1}
                stickyLeftColumns={FIXED_COLUMNS_COUNT}
            />
        </Box>
    );
};

export default PriceListGrid;
